import { findCountry } from '@/lib/data/countries'
import { SPACING } from '@/lib/brand'
import { BREAKPOINTS } from '@/lib/theme/breakpoints'
import type { TourCatalogExperience } from '@/lib/types/tours.types'

/**
 * Ruta del Pacífico y Oriente Mágico son nacionales, no tipos ni chips.
 * Si el API todavía las manda como categoría, no las pintamos.
 */
export const NATIONAL_THEME_CATEGORY_CODES = new Set(['ruta-del-pacifico', 'oriente-magico'])

/** Países del catálogo: Centroamérica (la operación de Tours nace ahí). */
export const CATALOG_COUNTRY_ISO2S = ['SV', 'GT', 'HN', 'NI', 'CR', 'PA', 'BZ'] as const

export const CATALOG_COUNTRIES = CATALOG_COUNTRY_ISO2S.flatMap((iso2) => {
  const country = findCountry(iso2)
  return country ? [country] : []
})

export function catalogCategoryLabel(
  experience: TourCatalogExperience,
  isEnglish: boolean,
): string | null {
  const category = experience.category
  if (!category || NATIONAL_THEME_CATEGORY_CODES.has(category.code)) return null
  return isEnglish ? category.nameEn : category.nameEs
}

export function formatTourDuration(durationMinutes: number): string {
  const hours = Math.floor(durationMinutes / 60)
  const minutes = durationMinutes % 60
  return hours > 0 ? `${hours}h${minutes > 0 ? ` ${minutes}m` : ''}` : `${durationMinutes}m`
}

/**
 * Same inset as AppTopNav (`SPACING.lg`). Catalog and detail canvas must
 * line up with the logo / nav cluster, not sit inside a second, wider gutter.
 */
export function catalogCanvasPadX(): number {
  return SPACING.lg
}

/**
 * Tour detail cover. A 16:9 / 2.2 full-width hero ate the first viewport
 * (~630–780px on 1440). The band is a short cinematic strip: 2.8 (~21:9+)
 * with a 380px ceiling so 1440×900 and native tablet landscape still show
 * title, chips and the booking rail without scrolling.
 *
 * `isDesktop` is large web only — iPad / Android tablets are `isTablet`.
 * Editorial (desktop OR tablet) shares this short hero. Phone stays 16:9
 * of the column with a 220px ceiling so landscape is not a splash either.
 */
export const TOUR_DETAIL_COVER_ASPECT = 2.8
export const TOUR_DETAIL_COVER_ASPECT_PHONE = 16 / 9
export const TOUR_DETAIL_COVER_MAX_HEIGHT = 380
export const TOUR_DETAIL_COVER_MAX_HEIGHT_PHONE = 220

export function tourDetailCoverAspect(editorial: boolean): number {
  return editorial ? TOUR_DETAIL_COVER_ASPECT : TOUR_DETAIL_COVER_ASPECT_PHONE
}

export function tourDetailCoverMaxHeight(editorial: boolean): number {
  return editorial ? TOUR_DETAIL_COVER_MAX_HEIGHT : TOUR_DETAIL_COVER_MAX_HEIGHT_PHONE
}

/** Two-column from `lg` on any editorial canvas — web desktop and native tablet landscape. */
export function tourDetailTwoColumn(editorial: boolean, width: number): boolean {
  return editorial && width >= BREAKPOINTS.lg
}

/**
 * Editorial grid density. Phone stays one column (FlashList). Tablet fills
 * two photographic tiles. From `lg` (1024) the grid goes to three so a
 * 1440 desktop is a magazine spread, not two fat cards.
 *
 * A featured 16:9 hero was rejected: it ate the first viewport and left
 * the rest of the catalog feeling like leftovers.
 */
export function catalogGridColumns(width: number, editorial: boolean): number {
  if (!editorial) return 1
  if (width >= BREAKPOINTS.lg) return 3
  return 2
}

export function catalogTileStyle(columns: number) {
  if (columns >= 3) return { flexBasis: '31%' as const, flexGrow: 1, minWidth: 0 }
  if (columns >= 2) return { flexBasis: '48%' as const, flexGrow: 1, minWidth: 0 }
  return { flexBasis: '100%' as const, flexGrow: 1, minWidth: 0 }
}
